<template>
  <div class="settings-tab">
    <header class="page-header">
      <div class="header-left">
        <h1 class="page-title">⚙️ Настройки системы</h1>
        <p class="page-subtitle">Управление параметрами работы системы</p>
      </div>
      <button @click="fetchSettings" class="btn-refresh" :disabled="loading">
        <span class="refresh-icon">🔄</span>
        Обновить
      </button>
    </header>

    <!-- LOADING STATE -->
    <div v-if="loading && settings.length === 0" class="loading-container">
      <div class="loading-spinner"></div>
      <p class="loading-text">Загрузка настроек...</p>
    </div>

    <!-- ERROR STATE -->
    <div v-else-if="error" class="error-container">
      <div class="error-icon">⚠️</div>
      <p class="error-title">Ошибка загрузки данных</p>
      <p class="error-message">{{ error }}</p>
      <button @click="fetchSettings" class="btn-primary">Повторить попытку</button>
    </div>

    <!-- SETTINGS GRID -->
    <div v-else class="settings-grid">
      <div v-for="setting in settings" :key="setting.setting_key" class="setting-card">
        <div class="setting-header">
          <div class="setting-icon">⚙️</div>
          <div class="setting-info">
            <h3 class="setting-title">{{ getSettingTitle(setting.setting_key) }}</h3>
            <p class="setting-description">{{ setting.description }}</p>
          </div>
        </div>

        <div class="setting-body">
          <!-- Number input -->
          <div v-if="isNumberSetting(setting.setting_key)" class="setting-control">
            <input
              v-model.number="editValues[setting.setting_key]"
              type="number"
              :min="getMinValue(setting.setting_key)"
              :max="getMaxValue(setting.setting_key)"
              class="setting-input"
              :disabled="saving === setting.setting_key || !hasPermission('change_settings')"
            />
            <span class="setting-unit">{{ getUnit(setting.setting_key) }}</span>
          </div>

          <!-- Text input -->
          <div v-else-if="isTextSetting(setting.setting_key)" class="setting-control">
            <input
              v-model="editValues[setting.setting_key]"
              type="text"
              class="setting-input"
              :disabled="saving === setting.setting_key || !hasPermission('change_settings')"
            />
          </div>

          <!-- Boolean toggle -->
          <div v-else-if="isBooleanSetting(setting.setting_key)" class="setting-control">
            <label class="toggle-switch">
              <input
                v-model="editValues[setting.setting_key]"
                type="checkbox"
                :disabled="saving === setting.setting_key || !hasPermission('change_settings')"
              />
              <span class="toggle-slider"></span>
            </label>
            <span class="toggle-label">
              {{ editValues[setting.setting_key] ? 'Включено' : 'Выключено' }}
            </span>
          </div>
        </div>

        <div class="setting-footer">
          <div class="setting-meta">
            <span class="setting-modified" v-if="setting.date_modified">
              Изменено: {{ formatDate(setting.date_modified) }}
            </span>
          </div>
          <button
            v-if="hasPermission('change_settings')"
            @click="saveSetting(setting)"
            class="btn-save"
            :disabled="saving === setting.setting_key || !hasChanged(setting)"
          >
            <span v-if="saving === setting.setting_key">⏳ Сохранение...</span>
            <span v-else-if="hasChanged(setting)">💾 Сохранить</span>
            <span v-else>✓ Сохранено</span>
          </button>
        </div>
      </div>
    </div>

    <!-- INFO SECTION -->
    <div class="info-section" v-if="settings.length > 0">
      <div class="info-icon">ℹ️</div>
      <div class="info-content">
        <h4 class="info-title">О настройках</h4>
        <p class="info-text">
          Изменения настроек применяются немедленно после сохранения.
          Некоторые параметры могут потребовать перезагрузки страницы для полного применения.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import axios from 'axios'
import { usePermissions } from '@/composables/usePermissions'

const API_BASE = 'https://mpapi.premside.uz/api/admin'

export default {
  name: 'SettingsTab',
  setup() {
    const { hasPermission } = usePermissions()
    const settings = ref([])
    const loading = ref(true)
    const error = ref(null)
    const saving = ref(null)
    const editValues = reactive({})

    /**
     * Загрузка настроек
     */
    const fetchSettings = async () => {
      loading.value = true
      error.value = null

      try {
        const response = await axios.get(`${API_BASE}/settings`)
        settings.value = response.data.data || []

        // Инициализация значений для редактирования
        settings.value.forEach(setting => {
          editValues[setting.setting_key] = parseSettingValue(setting)
        })
      } catch (err) {
        console.error('Error fetching settings:', err)
        error.value = err.response?.data?.message || err.message || 'Не удалось загрузить настройки'
      } finally {
        loading.value = false
      }
    }

    /**
     * Парсинг значения настройки
     */
    const parseSettingValue = (setting) => {
      const value = setting.setting_value

      // Число
      if (isNumberSetting(setting.setting_key)) {
        return parseInt(value, 10) || 0
      }

      // Булево
      if (isBooleanSetting(setting.setting_key)) {
        return value === 'true' || value === '1' || value === 1
      }

      // Текст
      return value || ''
    }

    /**
     * Сохранение настройки
     */
    const saveSetting = async (setting) => {
      saving.value = setting.setting_key

      try {
        let value = editValues[setting.setting_key]

        // Конвертируем булево в строку
        if (isBooleanSetting(setting.setting_key)) {
          value = value ? 'true' : 'false'
        }

        await axios.put(`${API_BASE}/settings/${setting.setting_key}`, {
          value: String(value)
        })

        // Обновляем локальное значение
        setting.setting_value = String(value)
        setting.date_modified = new Date().toISOString()

      } catch (err) {
        console.error('Error saving setting:', err)
        alert(err.response?.data?.message || err.message || 'Не удалось сохранить настройку')
      } finally {
        saving.value = null
      }
    }

    /**
     * Проверка изменения значения
     */
    const hasChanged = (setting) => {
      const currentValue = editValues[setting.setting_key]
      const originalValue = parseSettingValue(setting)
      return currentValue !== originalValue
    }

    /**
     * Определение типа настройки автоматически
     */
    const getSettingType = (setting) => {
      const value = setting.setting_value
      const key = setting.setting_key

      // Явно указанные типы (приоритет)
      const numberSettings = ['orders_display_days', 'auto_refresh_interval']
      const booleanSettings = ['notification_enabled', 'auto_refresh_enabled']

      if (numberSettings.includes(key)) return 'number'
      if (booleanSettings.includes(key)) return 'boolean'

      // Автоопределение по значению
      if (value === 'true' || value === 'false' || value === '0' || value === '1') {
        return 'boolean'
      }

      if (!isNaN(value) && value !== '' && !key.includes('url') && !key.includes('name')) {
        return 'number'
      }

      return 'text'
    }

    const isNumberSetting = (key) => {
      const setting = settings.value.find(s => s.setting_key === key)
      return setting ? getSettingType(setting) === 'number' : false
    }

    const isTextSetting = (key) => {
      const setting = settings.value.find(s => s.setting_key === key)
      return setting ? getSettingType(setting) === 'text' : false
    }

    const isBooleanSetting = (key) => {
      const setting = settings.value.find(s => s.setting_key === key)
      return setting ? getSettingType(setting) === 'boolean' : false
    }

    /**
     * Получить заголовок настройки
     */
    const getSettingTitle = (key) => {
      const titles = {
        'orders_display_days': 'Период отображения заказов',
        'auto_refresh_interval': 'Интервал автообновления',
        'notification_enabled': 'Уведомления',
        'auto_refresh_enabled': 'Автообновление',
        'api_url': 'URL API',
        'company_name': 'Название компании'
      }

      // Если нет в списке, создаем понятное название
      if (titles[key]) return titles[key]

      // Преобразуем snake_case в читаемый формат
      return key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    }

    /**
     * Получить единицу измерения
     */
    const getUnit = (key) => {
      const units = {
        'orders_display_days': 'дней',
        'auto_refresh_interval': 'секунд'
      }

      // Автоопределение по названию ключа
      if (units[key]) return units[key]

      if (key.includes('days')) return 'дней'
      if (key.includes('hours')) return 'часов'
      if (key.includes('minutes')) return 'минут'
      if (key.includes('seconds') || key.includes('interval')) return 'секунд'
      if (key.includes('count') || key.includes('limit') || key.includes('max')) return 'шт.'

      return ''
    }

    /**
     * Минимальное значение
     */
    const getMinValue = (key) => {
      const mins = {
        'orders_display_days': 1,
        'auto_refresh_interval': 5
      }

      if (mins[key]) return mins[key]

      // Автоопределение минимума
      if (key.includes('days')) return 1
      if (key.includes('interval') || key.includes('timeout')) return 1

      return 0
    }

    /**
     * Максимальное значение
     */
    const getMaxValue = (key) => {
      const maxs = {
        'orders_display_days': 365,
        'auto_refresh_interval': 3600
      }

      if (maxs[key]) return maxs[key]

      // Автоопределение максимума
      if (key.includes('days')) return 365
      if (key.includes('hours')) return 24
      if (key.includes('minutes')) return 60
      if (key.includes('seconds') || key.includes('interval')) return 300
      if (key.includes('limit') || key.includes('max') || key.includes('count')) return 10000

      return 999999
    }

    /**
     * Форматирование даты
     */
    const formatDate = (dateString) => {
      if (!dateString) return 'Не изменялось'

      const date = new Date(dateString)
      const now = new Date()
      const diff = now - date
      const hours = Math.floor(diff / (1000 * 60 * 60))
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))

      if (hours < 1) {
        return 'Только что'
      } else if (hours < 24) {
        return `${hours} ч. назад`
      } else if (days < 7) {
        return `${days} дн. назад`
      } else {
        return date.toLocaleDateString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      }
    }

    onMounted(() => {
      fetchSettings()
    })

    return {
      settings,
      loading,
      error,
      saving,
      editValues,
      fetchSettings,
      saveSetting,
      hasChanged,
      isNumberSetting,
      isTextSetting,
      isBooleanSetting,
      getSettingTitle,
      getUnit,
      getMinValue,
      getMaxValue,
      formatDate,
      hasPermission
    }
  }
}
</script>

<style scoped>
.settings-tab {
  padding: 2rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
}

.header-left {
  flex: 1;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  color: #6b7280;
  font-size: 1rem;
}

.btn-refresh {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  color: #374151;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-refresh:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #d1d5db;
}

.btn-refresh:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* LOADING */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
}

.loading-spinner {
  width: 60px;
  height: 60px;
  border: 4px solid #e5e7eb;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  margin-top: 1.5rem;
  color: #6b7280;
  font-size: 1rem;
}

/* ERROR */
.error-container {
  text-align: center;
  padding: 4rem 2rem;
}

.error-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.error-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.error-message {
  color: #6b7280;
  margin-bottom: 2rem;
}

.btn-primary {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(99, 102, 241, 0.3);
}

/* SETTINGS GRID */
.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.setting-card {
  background: #fff;
  border-radius: 1rem;
  border: 1px solid #e5e7eb;
  padding: 1.5rem;
  transition: all 0.2s;
}

.setting-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.setting-header {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.setting-icon {
  font-size: 2rem;
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  border-radius: 0.75rem;
  flex-shrink: 0;
}

.setting-info {
  flex: 1;
}

.setting-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.25rem;
}

.setting-description {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;
}

.setting-body {
  margin-bottom: 1.5rem;
}

.setting-control {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.setting-input {
  flex: 1;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.2s;
}

.setting-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.setting-input:disabled {
  background: #f3f4f6;
  cursor: not-allowed;
}

.setting-unit {
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
}

/* TOGGLE SWITCH */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 28px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e1;
  transition: 0.3s;
  border-radius: 28px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}

.toggle-switch input:checked + .toggle-slider {
  background-color: #6366f1;
}

.toggle-switch input:checked + .toggle-slider:before {
  transform: translateX(22px);
}

.toggle-switch input:disabled + .toggle-slider {
  opacity: 0.5;
  cursor: not-allowed;
}

.toggle-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.setting-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid #f3f4f6;
}

.setting-meta {
  flex: 1;
}

.setting-modified {
  font-size: 0.75rem;
  color: #9ca3af;
}

.btn-save {
  padding: 0.5rem 1.25rem;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #fff;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-save:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #9ca3af;
}

/* INFO SECTION */
.info-section {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 0.75rem;
}

.info-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.info-content {
  flex: 1;
}

.info-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e40af;
  margin-bottom: 0.25rem;
}

.info-text {
  font-size: 0.875rem;
  color: #1e40af;
  line-height: 1.5;
}

/* RESPONSIVE */
@media (max-width: 768px) {
  .settings-grid {
    grid-template-columns: 1fr;
  }

  .page-header {
    flex-direction: column;
    gap: 1rem;
  }

  .btn-refresh {
    width: 100%;
    justify-content: center;
  }
}
</style>
